import get from 'lodash/get';
import * as eventsPageActions from '../../action_creators/events/events_page_action_creators';
import asyncRecircFeedActions from '../async_recirc_feed_actions';
import notFoundActionCreator from '../../action_creators/not_found_status_action_creator';
import log from '../../../services/logger_service';
import EventsModel from '../../../models/events';

const fetchSwoogoEvents = async store => {
  try {
    const response = await EventsModel.fetch(store);
    return { events: response };
  } catch (error) {
    // eslint-disable-next-line prettier/prettier
    log.error(
      error,
      `Error fetching data from Swoogo api: ${error}`
    );
    return null;
  }
};

/**
 * Fetch a specific video by ID
 * @param videoId string
 */
const fetchSeenVideo = async videoId => {
  const { SEEN_API_URL, SEEN_KEY } = process.env;
  const { fetchVideo } = await import('@entertainment-ai/mansueto');

  if (!videoId || videoId === 'undefined') return null;

  try {
    return await fetchVideo(videoId, SEEN_API_URL, SEEN_KEY);
  } catch (error) {
    log.error(error, `Error fetching video ${videoId} from SEEEN: ${error}`);
    return null;
  }
};

/**
 * Fetch all public Seeen videos
 * @param {Object[]=} thumbnailSizes - Array of desired thumbnail sizes
 * @param {string} thumbnailSizes[].width - Desired thumbnail width
 * @param {string} thumbnailSizes[].height - Desired thumbnail height
 */
const fetchSeenVideos = async thumbnailSizes => {
  const { SEEN_API_URL, SEEN_KEY } = process.env;
  const { fetchVideos } = await import('@entertainment-ai/mansueto');

  try {
    return await fetchVideos({
      apiUrl: SEEN_API_URL,
      apiKey: SEEN_KEY,
      pageSize: 21,
      thumbnailSizes
    });
  } catch (error) {
    log.error(error, `Error fetching video playlist from SEEEN: ${error}`);
    return null;
  }
};

/**
 * Fetch a Seen playlist by ID
 * @param {Object} fetchSeenPlaylistObj - Object containing playlist ID and thumbnail sizes
 * @param {string} id - Playlist ID
 * @param {Object[]=} thumbnailSizes - Array of desired thumbnail sizes
 * @param {string} thumbnailSizes[].width - Desired thumbnail width
 * @param {string} thumbnailSizes[].height - Desired thumbnail height
 */
const fetchSeenPlaylist = async (id, thumbnailSizes) => {
  const { SEEN_API_URL, SEEN_KEY } = process.env;
  const { fetchPlaylist } = await import('@entertainment-ai/mansueto');

  try {
    const playlist = await fetchPlaylist(
      id,
      SEEN_API_URL,
      SEEN_KEY,
      thumbnailSizes
    );

    return { playlist };
  } catch (error) {
    log.error(error, `Error fetching playlist ${id} from SEEEN: ${error}`);
    return null;
  }
};

export default function fetchEventsPage(params, store, view) {
  return function firstEventsPageDispatch(dispatch) {
    const { videoId } = params;

    // Begin - set loading state
    dispatch(eventsPageActions.eventsPageLoading({ view }));

    const seenPromises = [];
    switch (view) {
      case 'video':
        seenPromises.push(fetchSeenVideo(videoId));
        break;
      case 'discover':
        seenPromises.push(
          fetchSeenVideos([
            { width: 807, height: 454 },
            { width: 600, height: 338 },
            { width: 420, height: 236 }
          ])
        );
        break;
      case 'calendar':
        seenPromises.push(
          fetchSeenPlaylist('100', [
            { width: 810, height: 465 },
            { width: 600, height: 338 },
            { width: 420, height: 235 }
          ]),
          fetchSwoogoEvents(store)
        );
        break;
      default:
        break;
    }

    return Promise.all([
      asyncRecircFeedActions(params, store)(dispatch),
      ...seenPromises
    ])
      .then(function handleEventsPageData(response) {
        let responseObj = {};

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < response.length; i++) {
          responseObj = Object.assign(responseObj, response[i]);
        }

        const { events, video, videos, playlist } = responseObj;

        const pageData = {
          view,
          events,
          video,
          videos,
          playlist
        };

        dispatch(eventsPageActions.eventsPageLoaded(pageData, store));
        return true;
      })
      .catch(function handleEventsPageError(err) {
        const status = get(err, 'response.status') || 500;
        if (status === 404) {
          dispatch(notFoundActionCreator(404, 'NOT_FOUND_STATUS', store));
        } else {
          dispatch(notFoundActionCreator(500, 'ERROR_STATUS', store));
          log.error(
            err,
            `Error in Events page actions, message: ${err.message}`
          );
        }
        dispatch(eventsPageActions.eventsPageLoadError(err, store));
      });
  };
}
